import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Header from '../components/Header';
import MobileActionBar from '../components/MobileActionBar';
import {
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  ArrowRight,
  Wrench,
  Clock,
  AlertTriangle,
  Home,
  Shield,
  ChevronRight
} from 'lucide-react';
import { companyInfo } from '../data/mock';
const ReactiveMaintenance = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', postcode: '', message: '', honeypot: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.honeypot) return;
    setIsSubmitting(true);
    setSubmitError(false);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          properties_managed: formData.postcode,
          job_description: `[Reactive Maintenance Enquiry] ${formData.message}`,
          trade_type: 'reactive',
          source_website: 'expert-propertymaintenance.co.uk',
          source_page: '/reactive-property-maintenance'
        }),
      });
      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', phone: '', postcode: '', message: '', honeypot: '' });
      } else {
        setSubmitError(true);
      }
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  const typicalJobs = [
    { title: 'Damaged Doors', desc: 'Internal doors, external doors, door frames, hinges, locks and handles' },
    { title: 'Failed Locks & Ironmongery', desc: 'Lock replacements, broken handles, damaged letterboxes, security issues' },
    { title: 'Minor Leaks', desc: 'Dripping taps, leaking toilet cisterns, shower seals, radiator valves' },
    { title: 'Damaged Fixtures', desc: 'Broken shelves, towel rails, curtain poles, damaged fittings' },
    { title: 'Plaster Damage', desc: 'Holes in walls, cracked plaster, damp damage, nail pops' },
    { title: 'Joinery Repairs', desc: 'Damaged skirting, architrave repairs, loose floorboards' },
    { title: 'Wall & Ceiling Repairs', desc: 'Cracks, holes, damage from fittings, water staining' },
    { title: 'Flooring Issues', desc: 'Damaged laminate sections, loose tiles, threshold strips' },
    { title: 'Decorating Repairs', desc: 'Patch painting, touch-ups, making good damaged areas' },
    { title: 'General Tenant Faults', desc: 'Any maintenance issue reported by tenants' }
  ];
  const benefits = [
    'Single point of contact for all repairs',
    'Multi-trade capability — no need to organise separate contractors',
    'Clear communication throughout the repair process',
    'Photographs and updates provided',
    '£5 million public liability insurance',
    '12-month workmanship guarantee'
  ];
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Reactive Property Maintenance Manchester | Emergency Repairs | Expert Property Maintenance</title>
        <meta name="description" content="Reactive property maintenance in Manchester for landlords and letting agents. Fast response to tenant-reported repairs, damaged doors, plumbing issues, general maintenance. Call 07300 405045." />
        <link rel="canonical" href="https://expert-propertymaintenance.co.uk/reactive-property-maintenance" />
      </Helmet>
      <Header variant="default" companyPhone={companyInfo.phone} />
      {/* Hero */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-20 bg-gradient-to-b from-brand-navy to-brand-navy/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-brand-yellow/10 border border-brand-yellow/30 text-brand-yellow px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Wrench className="w-4 h-4" />
              Property Maintenance
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Reactive Property Maintenance
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              Fast response to urgent repairs and tenant-reported maintenance issues. One call to resolve 
              any property problem — from minor repairs to multi-trade jobs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold" asChild>
                <a href="#contact">
                  Report a Repair
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-brand-navy" asChild>
                <a href={`tel:${companyInfo.phone}`}>
                  <Phone className="w-5 h-5 mr-2" />
                  {companyInfo.phone}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
      {/* What is Reactive Maintenance */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-brand-navy mb-6">
                What is Reactive Maintenance?
              </h2>
              <p className="text-lg text-brand-slate mb-6">
                Reactive maintenance covers urgent or unexpected repairs that arise during a tenancy — 
                the issues tenants report that need prompt attention to maintain the property and keep 
                tenants satisfied.
              </p>
              <p className="text-brand-slate mb-6">
                Unlike planned maintenance, reactive repairs are often unscheduled. A door handle breaks, 
                a tap starts dripping, plaster gets damaged. These issues need addressing quickly to prevent 
                escalation and maintain your property's condition.
              </p>
              <p className="text-brand-slate mb-8">
                Expert Property Maintenance provides reliable reactive maintenance support for landlords 
                and letting agents. One contact handles carpentry, decorating, plastering, plumbing, 
                electrical and general repairs — so you don't need to coordinate multiple contractors.
              </p>
              <div className="space-y-3">
                {benefits.map((benefit) => (
                  <div key={benefit} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-brand-yellow flex-shrink-0 mt-0.5" />
                    <span className="text-brand-slate">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-brand-grey rounded-2xl p-8">
              <h3 className="text-xl font-bold text-brand-navy mb-6">Who This Service is For</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-white rounded-xl">
                  <Home className="w-6 h-6 text-brand-yellow flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-brand-navy">Landlords</h4>
                    <p className="text-sm text-brand-slate">Managing your own properties and needing reliable repair support</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-white rounded-xl">
                  <Clock className="w-6 h-6 text-brand-yellow flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-brand-navy">Letting Agents</h4>
                    <p className="text-sm text-brand-slate">Needing a contractor to handle tenant-reported maintenance</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-white rounded-xl">
                  <Shield className="w-6 h-6 text-brand-yellow flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-brand-navy">Property Managers</h4>
                    <p className="text-sm text-brand-slate">Looking for consistent, quality repairs across portfolios</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Typical Jobs */}
      <section className="py-16 bg-brand-grey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-brand-navy mb-4 text-center">
            Typical Reactive Repairs
          </h2>
          <p className="text-lg text-brand-slate text-center mb-12 max-w-2xl mx-auto">
            Common issues we handle for landlords and letting agents across Greater Manchester.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {typicalJobs.map((job) => (
              <Card key={job.title} className="border-slate-200 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-brand-navy mb-2">{job.title}</h3>
                  <p className="text-brand-slate text-sm">{job.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-12 bg-brand-grey border border-brand-yellow/30 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-brand-yellow flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold text-brand-navy mb-2">Important Note</h4>
                <p className="text-brand-slate text-sm">
                  We provide responsive maintenance but do not offer 24-hour emergency call-out. For genuine emergencies 
                  (gas leaks, major flooding, no heating in winter), tenants should contact the appropriate emergency services 
                  or a registered emergency contractor. We can typically respond to urgent repairs within 1-2 working days.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Related Services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-brand-navy mb-8">Related Services</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <a href="/planned-property-maintenance" className="p-4 bg-brand-grey rounded-xl hover:bg-brand-grey transition-colors">
              <h4 className="font-semibold text-brand-navy">Planned Maintenance</h4>
              <p className="text-sm text-brand-slate">Scheduled preventative maintenance</p>
            </a>
            <a href="/void-property-works" className="p-4 bg-brand-grey rounded-xl hover:bg-brand-grey transition-colors">
              <h4 className="font-semibold text-brand-navy">Void Property Works</h4>
              <p className="text-sm text-brand-slate">Complete void turnarounds</p>
            </a>
            <a href="/end-of-tenancy" className="p-4 bg-brand-grey rounded-xl hover:bg-brand-grey transition-colors">
              <h4 className="font-semibold text-brand-navy">End of Tenancy</h4>
              <p className="text-sm text-brand-slate">Repairs between tenancies</p>
            </a>
            <a href="/landlord-services" className="p-4 bg-brand-grey rounded-xl hover:bg-brand-grey transition-colors">
              <h4 className="font-semibold text-brand-navy">Landlord Services</h4>
              <p className="text-sm text-brand-slate">Complete landlord support</p>
            </a>
          </div>
        </div>
      </section>
      {/* Contact Form */}
      <section id="contact" className="py-16 bg-brand-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="text-white">
              <h2 className="text-3xl font-bold mb-6">Report a Repair</h2>
              <p className="text-slate-300 text-lg mb-8">
                Tell us about the repair needed and we will get back to you promptly with next steps.
              </p>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center">
                    <Phone className="w-6 h-6 text-brand-navy" />
                  </div>
                  <div>
                    <div className="text-slate-400 text-sm">Call us</div>
                    <a href={`tel:${companyInfo.phone}`} className="text-white font-bold text-lg hover:text-brand-yellow">
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center">
                    <Mail className="w-6 h-6 text-brand-navy" />
                  </div>
                  <div>
                    <div className="text-slate-400 text-sm">Email us</div>
                    <a href={`mailto:${companyInfo.email}`} className="text-white font-bold hover:text-brand-yellow">
                      {companyInfo.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-brand-navy" />
                  </div>
                  <div>
                    <div className="text-slate-400 text-sm">Covering</div>
                    <div className="text-white font-bold">Greater Manchester & Cheshire</div>
                  </div>
                </div>
              </div>
            </div>
            <Card className="bg-white border-0">
              <CardContent className="p-6 sm:p-8">
                {isSubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-brand-navy mb-2">Repair Reported</h3>
                    <p className="text-brand-slate">We will be in touch to discuss next steps.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <h3 className="text-xl font-bold text-brand-navy mb-4">Repair Details</h3>
                    <input type="text" name="website" value={formData.honeypot} onChange={(e) => setFormData({...formData, honeypot: e.target.value})} style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />
                    <div>
                      <label className="block text-sm font-medium text-brand-slate mb-1.5">Your Name *</label>
                      <Input required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="John Smith" className="h-12" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-brand-slate mb-1.5">Email *</label>
                        <Input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="john@email.com" className="h-12" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-brand-slate mb-1.5">Phone *</label>
                        <Input type="tel" required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="07xxx xxxxxx" className="h-12" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-slate mb-1.5">Property Postcode</label>
                      <Input value={formData.postcode} onChange={(e) => setFormData({...formData, postcode: e.target.value})} placeholder="e.g., M1 1AA" className="h-12" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-slate mb-1.5">Describe the Repair Needed *</label>
                      <Textarea required value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} placeholder="What needs fixing? Please include as much detail as possible..." rows={4} />
                    </div>
                    <Button type="submit" className="w-full bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold h-12 text-lg" disabled={isSubmitting}>
                      {isSubmitting ? 'Sending...' : 'Submit Repair Request'}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p>© {new Date().getFullYear()} Expert Property Maintenance</p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <a href="/" className="hover:text-brand-yellow">Home</a>
              <a href="/landlord-services" className="hover:text-brand-yellow">Landlord Services</a>
              <a href="/letting-agent-maintenance" className="hover:text-brand-yellow">Letting Agents</a>
              <a href="/#contact" className="hover:text-brand-yellow">Contact</a>
            </div>
          </div>
        </div>
      </footer>
      <MobileActionBar phone={companyInfo.phone} />
    </div>
  );
};
export default ReactiveMaintenance;
